import * as React from "react";
import { Button, Tooltip, tokens } from "@fluentui/react-components";
import {
    ArrowDownload20Regular,
    Attach20Regular,
    Checkmark20Regular,
    ChevronDown20Regular,
    ChevronRight20Regular,
    PersonAdd20Regular,
    Save20Regular,
} from "@fluentui/react-icons";
import type { MailAttachment } from "../../services/graphMailService";
import { attachmentKey } from "../../services/mailAttachments";
import { canPreviewInline } from "../documents/documentKind";
import { istVisitenkarte } from "./vcard";
import { anhaengeNutzlast, ZIEHTYP_MAIL_ANHANG } from "./postfachZug";

/**
 * Anhang-Leiste des Mail-Lesebereichs (T-369).
 *
 * Steht direkt unter dem Mail-Kopf ÜBER dem Text (wie in Outlook) — vorher
 * stand sie hinter dem ganzen Text bzw. hinter allen Abschnitten einer
 * Konversation und wurde übersehen. In einer Konversation sammelt sie die
 * Anhänge aller Nachrichten; stammen sie aus mehr als einer Nachricht, trägt
 * jeder Anhang den Hinweis, aus welcher Nachricht er kommt (`herkunft`).
 *
 * Reine Darstellung: Laden, Speichern und Herunterladen bleiben in
 * `MailDetailWindow` (ein Ladepfad, ein Schreibweg; die Seitenleiste J6 liest
 * denselben State).
 */

/** Ab so vielen Anhängen wird die Liste gekürzt („+n weitere“). */
export const ANHANG_SICHTBAR_MAX = 6;

export type MailAnhangLeisteProps = {
    attachments: MailAttachment[];
    /** Anhänge-Laden ist fehlgeschlagen → Fehlerzeile mit „Neu laden“. */
    fehler: boolean;
    onRetry: () => void;
    offen: boolean;
    onToggle: () => void;
    /** Kontakt, bei dem „Beim Kontakt speichern“ ablegt; ohne Zuweisung gesperrt. */
    saveTargetContact: { displayName: string } | null | undefined;
    savedKeys: ReadonlySet<string>;
    savingKey: string | null;
    downloadingKey: string | null;
    vcfLaedtKey: string | null;
    saveMsg: string | null;
    onOpen: (att: MailAttachment) => void;
    onDownload: (att: MailAttachment) => void;
    onSave: (att: MailAttachment) => void;
    onSaveAll: () => void;
    onVisitenkarte: (att: MailAttachment) => void;
    /** Konversation: `messageId → „von Absender, Datum“`. Nur genutzt, wenn die
     *  Anhänge aus mehr als einer Nachricht stammen. */
    herkunft?: ReadonlyMap<string, string>;
    /**
     * Postfach der Mail — damit lassen sich die Anhänge in einen Vorgang oder
     * in die Dokumente eines Kontakts ziehen, auch in ein anderes
     * Browserfenster (User 01.11.2026). Ohne Angabe ist nichts ziehbar.
     */
    ziehMailbox?: string | null;
};

const ZIEH_TITEL = "Zum Ablegen in einen Vorgang, ein To-do oder die Dokumente eines Kontakts ziehen";

/** Drag-Props für einen oder mehrere Anhänge. */
function ziehbar(mailbox: string | null | undefined, anhaenge: readonly MailAttachment[]) {
    if (!mailbox || anhaenge.length === 0) return {};
    return {
        draggable: true as const,
        onDragStart: (e: React.DragEvent) => {
            e.dataTransfer.setData(ZIEHTYP_MAIL_ANHANG, anhaengeNutzlast(mailbox, anhaenge));
            e.dataTransfer.effectAllowed = "copy";
        },
    };
}

/** Anhang-Größe menschenlesbar (T2). */
export function formatAttachmentSize(bytes: number): string {
    if (!bytes || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MailAnhangLeiste(props: MailAnhangLeisteProps): React.ReactElement | null {
    const {
        attachments,
        fehler,
        onRetry,
        offen,
        onToggle,
        saveTargetContact,
        savedKeys,
        savingKey,
        downloadingKey,
        vcfLaedtKey,
        saveMsg,
        onOpen,
        onDownload,
        onSave,
        onSaveAll,
        onVisitenkarte,
        herkunft,
        ziehMailbox,
    } = props;
    const [alleZeigen, setAlleZeigen] = React.useState(false);

    if (!fehler && attachments.length === 0) return null;

    // Herkunft nur zeigen, wenn die Anhänge aus mehreren Nachrichten stammen —
    // sonst ist sie selbstverständlich und nur Rauschen.
    const mehrereQuellen = new Set(attachments.map((a) => a.messageId)).size > 1;
    const herkunftVon = (att: MailAttachment): string | null =>
        mehrereQuellen ? herkunft?.get(att.messageId) ?? null : null;

    const gekuerzt = !alleZeigen && attachments.length > ANHANG_SICHTBAR_MAX;
    const sichtbar = gekuerzt ? attachments.slice(0, ANHANG_SICHTBAR_MAX) : attachments;

    return (
        <div data-testid="mail-anhang-leiste">
            {/* Anhänge nicht ladbar → sichtbare Fehlerzeile statt still leer. */}
            {fehler && (
                <div
                    role="alert"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                        padding: "8px 12px",
                        marginBottom: attachments.length > 0 ? 10 : 0,
                        borderRadius: 8,
                        backgroundColor: tokens.colorStatusDangerBackground1,
                        border: `1px solid ${tokens.colorStatusDangerBorder1}`,
                        color: tokens.colorStatusDangerForeground1,
                        fontSize: 13,
                    }}
                >
                    <span style={{ flex: "1 1 auto", minWidth: 0 }}>Anhänge konnten nicht geladen werden.</span>
                    <Button appearance="secondary" size="small" onClick={onRetry}>
                        Neu laden
                    </Button>
                </div>
            )}
            {attachments.length > 0 && (
                <div>
                    {/* Einklappbare Anhang-Leiste (SP5): Kopf mit Zähler + „Alle speichern". */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <button
                            type="button"
                            onClick={onToggle}
                            aria-expanded={offen}
                            {...(attachments.length > 1 ? ziehbar(ziehMailbox, attachments) : {})}
                            title={ziehMailbox && attachments.length > 1 ? `Alle Anhänge: ${ZIEH_TITEL.charAt(0).toLowerCase()}${ZIEH_TITEL.slice(1)}` : undefined}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: "#6b5a66",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                fontFamily: "inherit",
                            }}
                        >
                            {offen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                            <Attach20Regular />
                            {attachments.length === 1 ? "1 Anhang" : `${attachments.length} Anhänge`}
                        </button>
                        {mehrereQuellen && herkunft && herkunft.size > 0 && (
                            <span style={{ fontSize: 12, color: "#8a7686" }}>aus mehreren Nachrichten dieser Konversation</span>
                        )}
                        <span style={{ flex: 1 }} />
                        {attachments.length > 1 && (
                            <Tooltip
                                content={saveTargetContact
                                    ? `Alle Anhänge bei ${saveTargetContact.displayName} als Dokument speichern`
                                    : "Zuweisung ist Voraussetzung"}
                                relationship="label"
                            >
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<Save20Regular />}
                                    disabled={!saveTargetContact || savingKey === "__all__"}
                                    onClick={onSaveAll}
                                >
                                    Alle beim Kontakt speichern
                                </Button>
                            </Tooltip>
                        )}
                    </div>

                    {offen && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                            {sichtbar.map((att) => {
                                const key = attachmentKey(att);
                                const saved = savedKeys.has(key);
                                const von = herkunftVon(att);
                                return (
                                    <div
                                        key={key}
                                        data-testid="mail-anhang-chip"
                                        {...ziehbar(ziehMailbox, [att])}
                                        title={[von ? `Anhang aus der Nachricht ${von}` : null, ziehMailbox ? ZIEH_TITEL : null]
                                            .filter(Boolean)
                                            .join(" — ") || undefined}
                                        style={{
                                            cursor: ziehMailbox ? "grab" : undefined,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                            border: "1px solid rgba(0,0,0,0.10)",
                                            borderRadius: 8,
                                            padding: "2px 4px 2px 8px",
                                            background: "#fff",
                                        }}
                                    >
                                        <Attach20Regular style={{ color: "#8a7686", fontSize: 16 }} />
                                        <button
                                            type="button"
                                            title={canPreviewInline(att.name, att.contentType) ? "Öffnen" : "Herunterladen"}
                                            onClick={() => onOpen(att)}
                                            style={{
                                                border: "none",
                                                background: "transparent",
                                                padding: 0,
                                                font: "inherit",
                                                cursor: "pointer",
                                                textAlign: "left",
                                                fontSize: 12.5,
                                                color: "#2d142b",
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            {att.name}
                                            {att.size ? ` · ${formatAttachmentSize(att.size)}` : ""}
                                            {von && (
                                                <span style={{ display: "block", fontSize: 11, color: "#8a7686" }}>
                                                    {von}
                                                </span>
                                            )}
                                        </button>
                                        {/* Visitenkarte: der einzige Anhang, aus dem
                                            unmittelbar ein Kontakt entsteht. */}
                                        {istVisitenkarte(att) && (
                                            <Tooltip content="Kontakt aus Visitenkarte anlegen" relationship="label">
                                                <Button
                                                    appearance="subtle"
                                                    size="small"
                                                    icon={<PersonAdd20Regular />}
                                                    disabled={vcfLaedtKey === key}
                                                    onClick={() => onVisitenkarte(att)}
                                                    aria-label={`Kontakt aus ${att.name} anlegen`}
                                                />
                                            </Tooltip>
                                        )}
                                        <Tooltip content="Herunterladen" relationship="label">
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                icon={<ArrowDownload20Regular />}
                                                disabled={downloadingKey === key}
                                                onClick={() => onDownload(att)}
                                                aria-label={`${att.name} herunterladen`}
                                            />
                                        </Tooltip>
                                        <Tooltip
                                            content={saveTargetContact
                                                ? (saved
                                                    ? "Beim Kontakt gespeichert"
                                                    : `Bei ${saveTargetContact.displayName} als Dokument speichern`)
                                                : "Zuweisung ist Voraussetzung"}
                                            relationship="label"
                                        >
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                icon={saved ? <Checkmark20Regular /> : <Save20Regular />}
                                                disabled={!saveTargetContact || saved || savingKey === key}
                                                onClick={() => onSave(att)}
                                                aria-label={`${att.name} beim Kontakt speichern`}
                                            />
                                        </Tooltip>
                                    </div>
                                );
                            })}
                            {attachments.length > ANHANG_SICHTBAR_MAX && (
                                <button
                                    type="button"
                                    onClick={() => setAlleZeigen((a) => !a)}
                                    style={{
                                        border: "1px dashed rgba(0,0,0,0.18)",
                                        borderRadius: 8,
                                        background: "transparent",
                                        padding: "4px 10px",
                                        fontFamily: "inherit",
                                        fontSize: 12.5,
                                        fontWeight: 600,
                                        color: "#6b5a66",
                                        cursor: "pointer",
                                    }}
                                >
                                    {gekuerzt ? `+${attachments.length - ANHANG_SICHTBAR_MAX} weitere` : "weniger anzeigen"}
                                </button>
                            )}
                        </div>
                    )}

                    {saveMsg && (
                        <div style={{ marginTop: 8, fontSize: 12, color: "#5b4a56" }} role="status">
                            {saveMsg}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
